/**
 * DashboardPage — the main authenticated dashboard.
 *
 * Contains all existing consent-engine views (Overview, Applications,
 * Consent Log, Notice Manager, Certificates, API Keys).
 * Uses the API client and DashboardShell for layout.
 */

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../../lib/constants';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from '../../components/DashboardShell';
import { DocShortcuts } from '../../components/DocShortcuts';
import { EmailVerificationBanner } from '../../components/EmailVerificationBanner';
import { EmptyState } from '../../components/EmptyState';
import { SetupChecklist } from '../../components/SetupChecklist';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { emitAnalyticsEvent } from '../../lib/analytics';
import { apiFetch, getApiKey, getNetworkErrorMessage, listDrafts, listMembers, listNoticeVersions, rollbackNoticeVersion, setApiKey } from '../../lib/api';
import type { Application, ConsentRecord, MultilingualNotice, NoticeDraftSummary, NoticeVersionSummary, Purpose } from '../../lib/types';
import { ApiKeysView } from './ApiKeysPage';
import { SettingsPage } from './SettingsPage';
import { ConsentNoticeDesigner } from './ConsentNoticeDesigner';
import { WorkflowPanel } from './WorkflowPanel';



export function DashboardPage() {
  const navigate = useNavigate();
  const { user, organisations, activeOrgId } = useAuth();
  const { addToast } = useToast();

  const [currentView, setCurrentView] = useState('overview');
  const [onboardingCompleted, setOnboardingCompleted] = useState(
    () => localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETED) === 'true',
  );
  const [successAppDetails, setSuccessAppDetails] = useState<{
    id: string;
    name: string;
    apiKey: string;
    date: string;
  } | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedAppId, setCopiedAppId] = useState(false);
  const [showCreatedKey, setShowCreatedKey] = useState(false);

  // Loaded data
  const [consents, setConsents] = useState<ConsentRecord[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [notices, setNotices] = useState<MultilingualNotice | null>(null);
  const [noticeVersions, setNoticeVersions] = useState<NoticeVersionSummary[]>([]);
  const [noticeVersionsLoading, setNoticeVersionsLoading] = useState(false);
  const [noticeVersionsError, setNoticeVersionsError] = useState<string | null>(null);
  // Workflow drafts + current user role (owner = reviewer/approver, member = drafter)
  const [drafts, setDrafts] = useState<NoticeDraftSummary[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'owner' | 'member'>('member');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);

  // Form inputs
  const [newAppName, setNewAppName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Notice editor
  const [isPublishingNotice, setIsPublishingNotice] = useState(false);
  const [isRollingBackNotice, setIsRollingBackNotice] = useState(false);

  // Active API key + app ID (from localStorage, set during onboarding or org switch)
  const apiKey = getApiKey();
  const activeAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';

  // Fetch dashboard data using API key (consent-engine routes are API-key auth)
  const fetchDashboardData = useCallback(async () => {
    if (!apiKey) {
      setIsLoadingData(false);
      return;
    }
    setIsLoadingData(true);
    setDataError(null);

    try {
      // Fetch consents + applications (API key auth)
      const [consentsData, appsData] = await Promise.allSettled([
        apiFetch<{ consents: ConsentRecord[] }>('/v1/consents', { useApiKey: true }),
        apiFetch<{ applications: Application[] }>('/v1/applications', { useApiKey: true }),
      ]);

      if (consentsData.status === 'fulfilled') {
        setConsents(consentsData.value.consents || []);
      }
      if (appsData.status === 'fulfilled') {
        setApplications(appsData.value.applications || []);
      }

      // Fetch notice for active app
      if (activeAppId) {
        try {
          const noticeData = await apiFetch<MultilingualNotice>(`/v1/notices/${activeAppId}`);
          setNotices(noticeData);
        } catch {
          // Notice fetch failure is non-critical
        }

        // Fetch notice version history
        try {
          setNoticeVersionsLoading(true);
          setNoticeVersionsError(null);
          const versionsData = await listNoticeVersions(activeAppId);
          setNoticeVersions(versionsData.notices || []);
        } catch {
          // Notice versions fetch failure is non-critical
          setNoticeVersionsError('Failed to load version history.');
        } finally {
          setNoticeVersionsLoading(false);
        }

        // Fetch workflow drafts (session-based, non-blocking)
        try {
          setDraftsLoading(true);
          setDraftsError(null);
          const draftsData = await listDrafts(activeAppId);
          setDrafts(draftsData.drafts || []);
        } catch {
          setDraftsError('Failed to load drafts.');
        } finally {
          setDraftsLoading(false);
        }

        // Resolve current user role (owner = reviewer/approver, member = drafter)
        if (user && activeOrgId) {
          try {
            const membersData = await listMembers(activeOrgId);
            const allMembers = [...(membersData.members || []), ...(membersData.pendingInvitations || [])];
            const me = allMembers.find((m) => m.email === user.email);
            setUserRole(me?.role === 'owner' ? 'owner' : 'member');
          } catch {
            // Non-critical - default to member
          }
        }
      }
    } catch (err) {
      const errorMsg = getNetworkErrorMessage(err, 'Failed to load dashboard data.');
      setDataError(errorMsg);
    } finally {
      setIsLoadingData(false);
    }
  }, [apiKey, activeAppId, user, activeOrgId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Redirect to onboarding if user has no organisations
  useEffect(() => {
    if (organisations.length === 0 && activeOrgId === null) {
      // Only redirect if we're not in a loading state — AuthContext handles that
      navigate('/onboarding');
    }
  }, [organisations, activeOrgId, navigate]);

  // Create Application
  const handleCreateApp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newAppName.trim()) return;
      setIsSubmitting(true);
      try {
        const data = await apiFetch<{ applicationId: string; name: string; apiKey: string }>(
          '/v1/applications',
          {
            method: 'POST',
            body: { name: newAppName },
            useApiKey: true,
          },
        );
        setNewAppName('');
        await fetchDashboardData();
        emitAnalyticsEvent('Application Created', { applicationId: data.applicationId });
        setSuccessAppDetails({
          id: data.applicationId,
          name: data.name,
          apiKey: data.apiKey,
          date: new Date().toLocaleDateString(),
        });
      } catch (err) {
        addToast({
          type: 'error',
          message: getNetworkErrorMessage(err, 'Failed to create application.'),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [newAppName, fetchDashboardData, addToast],
  );

  const handleDownloadJSON = (appDetails: { name: string; id: string; apiKey: string }) => {
    const creds = {
      organisation: organisations[0]?.name || 'Unknown Org',
      applicationId: appDetails.id,
      applicationName: appDetails.name,
      apiKey: appDetails.apiKey,
      createdAt: new Date().toISOString(),
      environment: 'staging',
    };
    const blob = new Blob([JSON.stringify(creds, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credentials_${appDetails.name.toLowerCase().replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    emitAnalyticsEvent('Credentials Downloaded', { applicationId: appDetails.id });
  };

  // Register Webhook
  const handleRegisterWebhook = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!webhookUrl.trim() || !webhookSecret.trim()) return;
      setIsSubmitting(true);
      try {
        await apiFetch('/v1/webhooks', {
          method: 'POST',
          body: { url: webhookUrl, secret: webhookSecret },
          useApiKey: true,
        });
        addToast({ type: 'success', message: 'Webhook registered successfully!' });
        setWebhookUrl('');
        setWebhookSecret('');
      } catch (err) {
        addToast({
          type: 'error',
          message: getNetworkErrorMessage(err, 'Failed to register webhook.'),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [webhookUrl, webhookSecret, addToast],
  );

  // Publish Notice
  const handlePublishNotice = useCallback(
    async (params: { enTitle: string; enPurposes: Purpose[]; hiTitle: string; hiPurposes: Purpose[] }) => {
      setIsPublishingNotice(true);
      try {
        await apiFetch('/v1/notices', {
          method: 'POST',
          body: {
            version: (notices?.version || 1) + 1,
            content: {
              en: { title: params.enTitle, purposes: params.enPurposes },
              hi: { title: params.hiTitle, purposes: params.hiPurposes },
            },
          },
          useApiKey: true,
        });
        addToast({ type: 'success', message: 'Notice published successfully!' });
        await fetchDashboardData();
      } catch (err) {
        addToast({
          type: 'error',
          message: getNetworkErrorMessage(err, 'Failed to publish notice.'),
        });
      } finally {
        setIsPublishingNotice(false);
      }
    },
    [notices, addToast, fetchDashboardData],
  );

  // Rollback Notice — creates a new version from a historical notice
  const handleRollbackNotice = useCallback(
    async (noticeId: string, fromVersion: number) => {
      setIsRollingBackNotice(true);
      try {
        const result = await rollbackNoticeVersion(noticeId);
        addToast({
          type: 'success',
          message: `Rolled back from v${fromVersion}. New version v${result.version} is now live.`,
        });
        // Refresh notice, version history, and metadata without a page reload.
        await fetchDashboardData();
      } catch (err) {
        addToast({
          type: 'error',
          message: getNetworkErrorMessage(err, 'Failed to roll back notice.'),
        });
      } finally {
        setIsRollingBackNotice(false);
      }
    },
    [addToast, fetchDashboardData],
  );

  const downloadCertificate = useCallback(async () => {
    if (!activeAppId) return;
    setIsSubmitting(true);
    try {
      const blob = await apiFetch<Blob>(`/v1/certificates/${activeAppId}`, {
        useApiKey: true,
        blob: true,
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `dpdp_compliance_certificate_${activeAppId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      addToast({ type: 'success', message: 'Certificate downloaded successfully!' });
    } catch (err) {
      addToast({
        type: 'error',
        message: getNetworkErrorMessage(err, 'Certificate generation failed.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [activeAppId, addToast]);

  // Export CSV
  const exportConsentsCSV = useCallback(() => {
    if (consents.length === 0) return;
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      ['Consent ID,Principal Hash,Notice Version,Status,Created At,Updated At'].join(',') +
      '\n' +
      consents
        .map(
          (c) =>
            `${c.id},${c.principal_hash},${c.notice_version},${c.status},${new Date(c.created_at * 1000).toLocaleString()},${new Date(c.updated_at * 1000).toLocaleString()}`,
        )
        .join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `consent_log_${activeAppId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast({ type: 'success', message: 'Consent log exported successfully!' });
  }, [consents, activeAppId, addToast]);

  // Calculations
  const totalConsents = consents.length;
  const activeConsents = consents.filter((c) => c.status === 'active').length;
  const withdrawnConsents = totalConsents - activeConsents;
  const withdrawalRate = totalConsents > 0 ? (withdrawnConsents / totalConsents) * 100 : 0;
  const complianceScore = notices ? (withdrawalRate > 50 ? 92 : 98) : 50;

  return (
    <DashboardShell currentView={currentView} onNavigate={setCurrentView} activeAppId={activeAppId}>
      <EmailVerificationBanner />
      {dataError && (
        <div className="mb-6 rounded-xl border border-red-800 bg-red-950/30 px-4 py-3 flex items-center gap-2 text-sm text-red-300">
          <span>⚠️</span> {dataError}
          <button
            onClick={fetchDashboardData}
            className="ml-auto text-xs text-red-400 underline hover:text-red-300"
          >
            Retry
          </button>
        </div>
      )}

      {isLoadingData && currentView !== 'overview' && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="xl" />
        </div>
      )}

      {/* OVERVIEW */}
      {currentView === 'overview' && (
        <div className="space-y-8">
          {/* Resume Setup Banner */}
          {(() => {
            let completedSteps = 1; // Org is created
            if (applications.length > 0) completedSteps += 1;
            if (localStorage.getItem(STORAGE_KEYS.SETUP_COPIED_KEY) === 'true') completedSteps += 1;
            if (notices !== null) completedSteps += 1;
            if (consents.length > 0) completedSteps += 3; // Widget installed, tested consent, go live

            if (!onboardingCompleted && completedSteps < 7) {
              return (
                <div className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-500 animate-pulse"></span>
                      Continue Setup
                    </h3>
                    <p className="mt-1 text-xs text-slate-400">
                      You have completed {completedSteps} of 7 steps required to integrate consent
                      collection.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/onboarding')}
                      className="rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-blue-500 transition"
                    >
                      Resume Setup
                    </button>
                    <button
                      onClick={() => {
                        localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETED, 'true');
                        setOnboardingCompleted(true);
                      }}
                      className="rounded-xl border border-slate-800 bg-slate-900/30 px-5 py-2.5 text-xs font-semibold text-slate-400 hover:bg-slate-900/60 transition"
                    >
                      Dismiss Setup Card
                    </button>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-white">Overview</h2>
              <p className="mt-1 text-sm text-slate-400">
                Consent analytics and compliance tracking.
              </p>
            </div>
            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/10 px-4 py-2.5 text-xs text-yellow-400 font-semibold tracking-wide">
              ⚠️ Nov 13, 2026 Deadlines: Active Tracking
            </div>
          </div>

          {isLoadingData ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 animate-pulse"
                >
                  <div className="mb-4 h-3 w-24 rounded bg-slate-800"></div>
                  <div className="h-10 w-20 rounded bg-slate-800"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 flex flex-col justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Total Consents
                </span>
                <span className="mt-2 text-4xl font-extrabold text-white">{totalConsents}</span>
              </div>
              <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 flex flex-col justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Active Consents
                </span>
                <span className="mt-2 text-4xl font-extrabold text-emerald-400">
                  {activeConsents}
                </span>
              </div>
              <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 flex flex-col justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Withdrawal Rate
                </span>
                <span className="mt-2 text-4xl font-extrabold text-red-400">
                  {withdrawalRate.toFixed(1)}%
                </span>
              </div>
              <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6 flex flex-col justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Compliance Score
                </span>
                <span className="mt-2 text-4xl font-extrabold text-blue-400">
                  {complianceScore}%
                </span>
              </div>
            </div>
          )}

          {/* Setup Checklist widget */}
          <SetupChecklist
            applicationsCount={applications.length}
            hasNotice={notices !== null}
            consentsCount={consents.length}
            onNavigate={setCurrentView}
          />

          <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-8">
            <h3 className="text-base font-bold text-white uppercase tracking-wider">
              Consent Acquisition Velocity
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              Acquisition trend for active consent events.
            </p>
            <div className="mt-6 flex h-48 w-full items-end">
              <svg className="h-full w-full" viewBox="0 0 800 150">
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0 150 Q 100 110 200 130 T 400 70 T 600 50 T 800 20"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="drop-shadow-[0_2px_8px_rgba(59,130,246,0.6)]"
                />
                <path
                  d="M 0 150 Q 100 110 200 130 T 400 70 T 600 50 T 800 20 L 800 150 Z"
                  fill="url(#chartGradient)"
                  className="fill-[url(#chartGradient)]"
                />
              </svg>
            </div>
          </div>

          {/* Developer documentation shortcuts */}
          <div className="space-y-4 pt-4">
            <h3 className="text-lg font-bold text-white">Developer Guides &amp; Documentation</h3>
            <DocShortcuts />
          </div>
        </div>
      )}

      {/* APPLICATIONS */}
      {currentView === 'applications' && !isLoadingData && (
        <div className="space-y-8">
          <div>
            <h2 className="text-3xl font-extrabold text-white">Applications &amp; Integration</h2>
            <p className="mt-1 text-sm text-slate-400">
              Manage client applications, retrieve credentials, and set up event sync webhooks.
            </p>
          </div>

          {/* Context explanation card */}
          <div className="rounded-xl border border-slate-900 bg-slate-900/10 p-6 space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <span>📱</span> What is an Application?
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Applications represent separate software interfaces (e.g., website, client portal,
              mobile app, API backend) that collect personal data. Every application is isolated,
              maintaining its own:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 text-[11px] text-slate-500">
              <div className="bg-slate-950 p-3 rounded-lg border border-slate-900">
                <span className="text-blue-400 font-semibold block mb-1">🔐 API Keys</span>
                Client-side or server-side auth token.
              </div>
              <div className="bg-slate-950 p-3 rounded-lg border border-slate-900">
                <span className="text-blue-400 font-semibold block mb-1">📜 Consent Notices</span>
                Bilingual descriptions of data purposes.
              </div>
              <div className="bg-slate-950 p-3 rounded-lg border border-slate-900">
                <span className="text-blue-400 font-semibold block mb-1">📝 Audit Logs</span>
                Signed compliance certificate registers.
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Create Application Form */}
            <div className="space-y-6 rounded-2xl border border-slate-900 bg-slate-900/20 p-8">
              <h3 className="text-lg font-bold text-white">Create Application</h3>
              <p className="text-xs text-slate-500">
                Register a new client application to generate a unique API Key.
              </p>
              <form onSubmit={handleCreateApp} className="space-y-4">
                <input
                  type="text"
                  placeholder="Application Name (e.g. Mobile App)"
                  value={newAppName}
                  onChange={(e) => setNewAppName(e.target.value)}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 outline-none"
                  disabled={isSubmitting}
                  maxLength={80}
                  required
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !newAppName.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" /> Creating...
                    </>
                  ) : (
                    'Create Application'
                  )}
                </button>
              </form>
            </div>

            {/* Webhook Registration Form */}
            <div className="space-y-6 rounded-2xl border border-slate-900 bg-slate-900/20 p-8 flex flex-col justify-between">
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white">Register Downstream Webhook</h3>
                <p className="text-xs text-slate-500">
                  Notify your backend database immediately when data principals grant or withdraw
                  consent.
                </p>
                <form onSubmit={handleRegisterWebhook} className="space-y-4">
                  <input
                    type="url"
                    placeholder="Webhook Target URL (https://...)"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 outline-none"
                    disabled={isSubmitting}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Signature Verification Secret"
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 outline-none"
                    disabled={isSubmitting}
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting || !webhookUrl.trim() || !webhookSecret.trim()}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
                    >
                      {isSubmitting ? <Spinner size="sm" /> : 'Register Webhook'}
                    </button>
                    <button
                      type="button"
                      disabled
                      className="rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-xs font-semibold text-slate-600 cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      Test Webhook{' '}
                      <span className="text-[7px] border border-slate-800 px-1 py-0.5 rounded text-slate-600 font-bold uppercase">
                        Soon
                      </span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Webhook Documentation Guide & Example Payload */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-1 space-y-4 rounded-xl border border-slate-900 bg-slate-950/40 p-6">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                💡 Webhook Details
              </h4>
              <div className="space-y-3 text-xs text-slate-500 leading-relaxed">
                <div>
                  <span className="font-semibold text-slate-400 block mb-0.5">Why use it?</span>
                  Synchronise consent records to your local user database to enable/disable
                  processing.
                </div>
                <div>
                  <span className="font-semibold text-slate-400 block mb-0.5">
                    Supported Events
                  </span>
                  <code className="text-blue-400">consent.granted</code>,{' '}
                  <code className="text-blue-400">consent.withdrawn</code>
                </div>
                <div>
                  <span className="font-semibold text-slate-400 block mb-0.5">Retry Behaviour</span>
                  We retry delivery using exponential backoff up to 5 times over 24 hours if your
                  target responds with a non-2xx status code.
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4 rounded-xl border border-slate-900 bg-slate-950/40 p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    📋 Example Payload
                  </h4>
                  <button
                    onClick={() => {
                      const payloadStr = JSON.stringify(
                        {
                          event: 'consent.granted',
                          timestamp: Math.floor(Date.now() / 1000),
                          data: {
                            consentId: 'con_7b055ca5-1959-4616-830b-f213b66bfc38',
                            principalHash:
                              '5a3ed2d8bf7d6f7e3d1eccceb3dc0114e70f9232aebc1f508390503470c9e42b',
                            noticeVersion: 1,
                            purposeIds: ['analytics', 'marketing'],
                            status: 'active',
                          },
                        },
                        null,
                        2,
                      );
                      navigator.clipboard.writeText(payloadStr);
                      addToast({ type: 'success', message: 'Example payload copied!' });
                    }}
                    className="text-[10px] text-blue-400 hover:text-blue-300 font-semibold"
                  >
                    Copy JSON
                  </button>
                </div>
                <pre className="mt-3 p-3 bg-slate-950 rounded-lg text-[10px] font-mono text-slate-400 border border-slate-900 overflow-x-auto leading-relaxed max-h-36">
                  {`{
  "event": "consent.granted",
  "timestamp": 1784528172,
  "data": {
    "consentId": "con_7b055ca5-1959-4616-830b-f213b66bfc38",
    "principalHash": "5a3ed2d8bf7d6f7e3d1eccceb3dc0114e70f9232aebc1f508390503470c9e42b",
    "noticeVersion": 1,
    "purposeIds": ["analytics", "marketing"],
    "status": "active"
  }
}`}
                </pre>
              </div>
            </div>
          </div>

          {/* Registered Applications List */}
          <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-8">
            <h3 className="mb-6 text-lg font-bold text-white">Registered Applications</h3>
            {applications.length === 0 ? (
              <EmptyState
                icon="📱"
                title="No Applications Yet"
                description="Create your first application to start collecting consents."
                action={
                  <button
                    onClick={() => setNewAppName('My First App')}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
                  >
                    Create Application
                  </button>
                }
              />
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-slate-900 bg-slate-950 p-4 hover:border-slate-800 transition"
                  >
                    <div>
                      <p className="text-sm font-semibold text-white">{app.name}</p>
                      <p className="mt-0.5 font-mono text-[10px] text-slate-500">{app.id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(app.id);
                          addToast({ type: 'success', message: 'Application ID copied!' });
                        }}
                        className="rounded bg-slate-900 border border-slate-800 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-200 transition"
                      >
                        Copy ID
                      </button>
                      <button
                        onClick={() => {
                          localStorage.setItem(STORAGE_KEYS.APP_ID, app.id);
                          fetchDashboardData();
                          addToast({
                            type: 'success',
                            message: `Switched view context to ${app.name}`,
                          });
                        }}
                        className="rounded bg-blue-600/10 border border-blue-500/25 px-2.5 py-1 text-[10px] text-blue-400 hover:bg-blue-600/20 transition"
                      >
                        Switch to Application
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONSENT LOG */}
      {currentView === 'consent-log' && !isLoadingData && (
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-extrabold text-white">Consent Log</h2>
              <p className="mt-1 text-sm text-slate-400">
                Searchable and exportable audit registers.
              </p>
            </div>
            <button
              onClick={exportConsentsCSV}
              disabled={consents.length === 0}
              className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-semibold text-slate-300 shadow hover:bg-slate-800 disabled:opacity-50"
            >
              Export CSV
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-900 bg-slate-900/20">
            {consents.length === 0 ? (
              <EmptyState
                icon="📝"
                title="No Consent Records Yet"
                description="Once your users start giving consent, records will appear here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead className="border-b border-slate-900 bg-slate-950 text-xs font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-6 py-4">Consent ID</th>
                      <th className="px-6 py-4">Principal Hash</th>
                      <th className="px-6 py-4">Version</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Recorded At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-900">
                    {consents.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-900/30">
                        <td className="px-6 py-4 font-mono text-xs font-semibold text-white">
                          {c.id}
                        </td>
                        <td className="max-w-xs truncate px-6 py-4 font-mono text-xs">
                          {c.principal_hash}
                        </td>
                        <td className="px-6 py-4 text-xs">{c.notice_version}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              c.status === 'active'
                                ? 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
                                : 'border border-red-500/20 bg-red-500/10 text-red-400'
                            }`}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-xs text-slate-500">
                          {new Date(c.created_at * 1000).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONSENT NOTICE DESIGNER */}
      {currentView === 'notice-manager' && (
        <div className="space-y-8">
          <WorkflowPanel
            drafts={drafts}
            draftsLoading={draftsLoading}
            draftsError={draftsError}
            userRole={userRole}
            onWorkflowChanged={fetchDashboardData}
          />
          <ConsentNoticeDesigner
          notices={notices}
          isLoading={isLoadingData}
          onPublish={async (data) => {
            await handlePublishNotice(data);
          }}
          isPublishing={isPublishingNotice}
          onRollback={async (noticeId, fromVersion) => {
            await handleRollbackNotice(noticeId, fromVersion);
          }}
          isRollingBack={isRollingBackNotice}
          noticeVersions={noticeVersions}
          noticeVersionsLoading={noticeVersionsLoading}
          noticeVersionsError={noticeVersionsError}
          applicationId={activeAppId}
        />
        </div>
      )}

      {/* COMPLIANCE CERTIFICATE */}
      {currentView === 'compliance-certificate' && !isLoadingData && (
        <div className="space-y-8">
          <div>
            <h2 className="text-3xl font-extrabold text-white">Compliance Certificate</h2>
            <p className="mt-1 text-sm text-slate-400">
              Generate, download, and verify digital compliance reports.
            </p>
          </div>

          {consents.length === 0 ? (
            <EmptyState
              icon="📜"
              title="No Certificates Available"
              description="Compliance certificates will appear here once you have collected user consents."
              action={
                <button
                  onClick={() => setCurrentView('overview')}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
                >
                  View Setup Guide
                </button>
              }
            />
          ) : (
            <div className="flex flex-col items-center justify-center space-y-6 rounded-2xl border border-slate-900 bg-slate-900/20 p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-3xl font-bold text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                ✓
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-bold text-white">Certificate Generation Ready</h3>
                <p className="max-w-md text-sm text-slate-400">
                  Generate a signed PDF certificate showing active user consent logs, withdrawal
                  ratios, and verification signatures.
                </p>
              </div>
              <button
                onClick={downloadCertificate}
                disabled={isSubmitting}
                className="flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-8 py-3 text-sm font-semibold text-white transition duration-200 hover:bg-emerald-500 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" /> Generating...
                  </>
                ) : (
                  'Generate & Download Certificate PDF'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* API KEYS */}
      {currentView === 'api-keys' && !isLoadingData && (
        <div className="space-y-8">
          <div>
            <h2 className="text-3xl font-extrabold text-white">API Keys</h2>
            <p className="mt-1 text-sm text-slate-400">Manage and revoke your API keys.</p>
          </div>
          <ApiKeysView />
        </div>
      )}

      {/* SETTINGS */}
      {currentView === 'settings' && <SettingsPage />}

      {/* Application Created Success Screen Overlay */}
      {successAppDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="w-full max-w-lg rounded-2xl border border-slate-800 bg-slate-900 p-8 shadow-2xl space-y-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🎉</span>
              <div>
                <h3 className="text-xl font-bold text-white">Application Created Successfully</h3>
                <p className="text-xs text-slate-400 mt-1">
                  Your new DPDP consent application is registered.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-xs text-yellow-300 leading-relaxed">
              <p className="font-bold flex items-center gap-1">⚠️ Security Notice</p>
              <p className="mt-1">
                Store this API Key securely. It will be required to integrate your application and
                cannot be retrieved again later.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Application Name
                </label>
                <p className="mt-1 text-sm text-slate-200 font-semibold">
                  {successAppDetails.name}
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Application ID
                </label>
                <div className="mt-1 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-3">
                  <span className="font-mono text-xs text-slate-300 truncate flex-1 pr-4">
                    {successAppDetails.id}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(successAppDetails.id);
                      setCopiedAppId(true);
                      setTimeout(() => setCopiedAppId(false), 2000);
                    }}
                    className="text-[11px] font-bold text-blue-400 hover:text-blue-300 transition"
                  >
                    {copiedAppId ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  API Key
                </label>
                <div className="mt-1 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-3">
                  <span className="font-mono text-xs text-slate-300 truncate flex-1 pr-4">
                    {showCreatedKey
                      ? successAppDetails.apiKey
                      : '••••••••••••••••••••••••••••••••••••••••'}
                  </span>
                  <button
                    onClick={() => setShowCreatedKey(!showCreatedKey)}
                    className="text-[11px] font-bold text-slate-500 hover:text-slate-300 transition px-2"
                  >
                    {showCreatedKey ? 'Hide' : 'Reveal'}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(successAppDetails.apiKey);
                      setCopiedKey(true);
                      localStorage.setItem(STORAGE_KEYS.SETUP_COPIED_KEY, 'true');
                      setTimeout(() => setCopiedKey(false), 2000);
                    }}
                    className="text-[11px] font-bold text-blue-400 hover:text-blue-300 transition"
                  >
                    {copiedKey ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-slate-500 block">Created Date</span>
                  <span className="text-slate-300 font-semibold mt-1 block">
                    {successAppDetails.date}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500 block">Status</span>
                  <span className="text-emerald-400 font-semibold mt-1 block flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>{' '}
                    Active
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => handleDownloadJSON(successAppDetails)}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-2.5 text-xs font-semibold text-slate-300 hover:bg-slate-900/60"
              >
                Download JSON Credentials
              </button>
              <a
                href="https://docs.dpdp-consent.example.com/guide/widget-integration"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900/30 py-2.5 text-xs font-semibold text-slate-300 hover:bg-slate-900/60 text-center flex items-center justify-center"
              >
                View Documentation
              </a>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                disabled
                className="flex-1 rounded-xl border border-slate-800 bg-slate-950 py-2.5 text-xs font-semibold text-slate-500 cursor-not-allowed flex items-center justify-center gap-1"
              >
                Generate SDK{' '}
                <span className="text-[8px] bg-slate-900 border border-slate-800 px-1 py-0.5 rounded text-slate-600 uppercase font-bold">
                  Soon
                </span>
              </button>
              <button
                onClick={() => {
                  setApiKey(successAppDetails.apiKey, successAppDetails.id);
                  fetchDashboardData();
                  setSuccessAppDetails(null);
                  addToast({
                    type: 'success',
                    message: `Switched to application "${successAppDetails.name}"`,
                  });
                }}
                className="flex-1 rounded-xl bg-blue-600 py-2.5 text-xs font-semibold text-white hover:bg-blue-500"
              >
                Switch to Application
              </button>
            </div>

            <div className="text-center">
              <button
                onClick={() => setSuccessAppDetails(null)}
                className="text-xs text-slate-500 hover:text-slate-300 underline font-medium"
              >
                Continue Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
